import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import { LoadingScreen, Button } from '../common'

interface AuthGuardProps {
  children: ReactNode
  redirectTo?: string
}

export function AuthGuard({ children, redirectTo = '/login' }: AuthGuardProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading, isInitialized, profile, _hasHydrated, setIsLoading, setInitialized } = useAuthStore()
  const [showRetry, setShowRetry] = useState(false)

  // If we have cached profile, consider auth ready (network will refresh in background)
  const hasCachedProfile = _hasHydrated && profile !== null
  const isAuthReady = isInitialized || hasCachedProfile

  // Safety fallback: only needed if no cached profile
  useEffect(() => {
    if (!isAuthReady && (!isInitialized || isLoading)) {
      const failSafe = setTimeout(() => {
        setIsLoading(false)
        setInitialized(true)
      }, 5000) // Increased to 5s to give network more time
      return () => clearTimeout(failSafe)
    }
  }, [isAuthReady, isInitialized, isLoading, setInitialized, setIsLoading])

  // Show retry button after 10 seconds of loading (only if no cached profile)
  useEffect(() => {
    if (!isAuthReady && (!isInitialized || isLoading)) {
      const retryTimer = setTimeout(() => {
        setShowRetry(true)
      }, 10000)
      return () => clearTimeout(retryTimer)
    }
    setShowRetry(false)
  }, [isAuthReady, isInitialized, isLoading])

  useEffect(() => {
    // Only redirect if we're sure there's no auth (not just loading)
    if (isAuthReady && !isLoading && !isAuthenticated() && !hasCachedProfile) {
      navigate(redirectTo, {
        replace: true,
        state: { from: location.pathname },
      })
    }
  }, [isAuthReady, isLoading, isAuthenticated, hasCachedProfile, navigate, redirectTo, location])

  // If we have cached profile, render children immediately (no loading state)
  if (hasCachedProfile) {
    return <>{children}</>
  }

  // Show loading while checking auth state (only for users without cached profile)
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <LoadingScreen message="Checking authentication..." />
        {showRetry && (
          <div className="mt-6 text-center">
            <p className="text-gray-500 mb-4">Taking longer than expected...</p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
            >
              Retry
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Not authenticated - will redirect
  if (!isAuthenticated()) {
    return <LoadingScreen message="Redirecting to login..." />
  }

  return <>{children}</>
}
